import Util from '../utils/util';

import { Packets } from '@kaetram/common/network';

import type Game from '../game';

export default class ChatController {
    private chatBox: HTMLElement = document.querySelector('#chat')!;
    private log: HTMLElement = document.querySelector('#chat-log')!;
    private input: HTMLInputElement = document.querySelector('#chat-input')!;
    private button = document.querySelector('#chat-button')!;

    private readonly fadingDuration = 5000;
    private fadingTimeout!: number | undefined;

    // Whether the user explicitly opened the chat (persists across blur).
    private opened = false;

    public constructor(private game: Game) {
        this.button.addEventListener('click', () => this.toggle());

        this.input.addEventListener('blur', () => {
            if (!this.opened) this.hideInput();
        });
    }

    /**
     * Handles key input from the handler. Pressing the ENTER
     * key will trigger the chat input similar to the button.
     * @param key The key identifier from the JQuery KeyDownEvent.
     */

    public keyDown(key: string): void {
        if (key === 'Enter' && this.input.value !== '') return this.send();
        if (key === 'Escape' || key === 'Enter') this.toggle();
    }

    /**
     * Opens the chat box and automatically types the private message command.
     * @param username The username of the friend we are messaging.
     */

    public privateMessage(username: string): void {
        if (this.inputVisible()) return;

        this.toggle();

        this.input.value = `/pm *${username}* `;
        this.input.focus();
    }

    /**
     * Adds an entry to the chat box with the source,
     * message, and optional colour (defaults to white).
     */

    public add(source: string, message: string, colour = '', notify = false): void {
        let element = this.createElement(source, message);

        element.style.color = colour || 'white';
        if (notify) element.style.fontWeight = 'bold';

        let atBottom = this.log.scrollHeight - this.log.scrollTop - this.log.clientHeight < 30;

        this.log.append(element);

        if (atBottom) this.log.scrollTop = this.log.scrollHeight;

        this.displayChatBox();

        if (!this.opened) this.hideChatBox();
    }

    private createElement(source: string, message: string): HTMLElement {
        let element = document.createElement('p'),
            safe = message
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/&lt;span style="color:(\w+);"&gt;/g, '<span style="color:$1;">')
                .replace(/&lt;\/span&gt;/g, '</span>');

        element.append(document.createTextNode(`${source} » `));

        let msg = document.createElement('span');
        msg.innerHTML = safe;
        element.append(msg);

        return element;
    }

    /**
     * Sends a packet to the server with the string
     * of the chat message in the input field.
     */

    public send(): void {
        this.game.socket.send(Packets.Chat, [this.input.value]);

        this.input.value = '';
        this.input.focus();
    }

    /**
     * Toggles the chat open/closed. Open = chatbox + input
     * stay visible until explicitly closed by the user.
     */

    public toggle(): void {
        this.clearTimeout();

        if (this.opened) this.close();
        else this.open();
    }

    /**
     * Opens the chat: shows chatbox + input, keeps them
     * visible until the user explicitly closes.
     */

    private open(): void {
        this.opened = true;
        this.button.classList.add('active');

        this.displayChatBox();

        Util.fadeIn(this.input);
        this.input.focus();

        this.log.scrollTop = this.log.scrollHeight;
    }

    /**
     * Closes the chat: hides the input and schedules
     * the chatbox fadeout. Preserves typed text.
     */

    private close(): void {
        this.opened = false;
        this.button.classList.remove('active');

        this.input.blur();
        Util.fadeOut(this.input);

        this.log.scrollTop = this.log.scrollHeight;

        this.hideChatBox();
    }

    /**
     * Hides only the input element (used on blur when
     * the chat was not explicitly opened by the user).
     */

    private hideInput(): void {
        this.button.classList.remove('active');

        Util.fadeOut(this.input);

        this.hideChatBox();
    }

    private displayChatBox(): void {
        Util.fadeIn(this.chatBox);
    }

    private hideChatBox(): void {
        this.clearTimeout();

        this.fadingTimeout = window.setTimeout(() => {
            if (!this.opened && !this.inputVisible()) Util.fadeOut(this.chatBox);
        }, this.fadingDuration);
    }

    public inputVisible(): boolean {
        return this.input.style.display === 'block';
    }

    /**
     * Cleans all the chat inputs and hides everything if
     * the input is visible.
     */

    public clear(): void {
        if (this.opened) this.toggle();

        this.clearTimeout();
    }

    public clearTimeout(): void {
        clearTimeout(this.fadingTimeout);
        window.clearTimeout(this.fadingTimeout);

        this.fadingTimeout = undefined;
    }
}
